/**
 * Business Subscriptions — Screen 1 seed data (Billing & Subscriptions).
 * One continuing billing record per Business.
 */

export const PLAN_FILTER_OPTIONS = ['Basic', 'Pro'];

export const SUBSCRIPTION_STATUS_OPTIONS = ['Trial', 'Active', 'Grace Period', 'Inactive'];

export const LAST_PAYMENT_STATUS_OPTIONS = [
  'No Payment Yet',
  'Pending',
  'Succeeded',
  'Failed',
  'Cancelled',
  'Partially Refunded',
  'Refunded',
  'Disputed',
];

/** @typedef {'None' | 'Downgrade to Basic' | 'Cancellation'} ScheduledChangeType */

/** Basic: 4 Quote deliveries / cycle · Pro: 15 Quote deliveries / cycle */
export const QUOTE_DELIVERY_ALLOWANCE_BY_PLAN = {
  Basic: 4,
  Pro: 15,
};

/**
 * @typedef {Object} BusinessSubscriptionRecord
 * @property {string} id
 * @property {string} businessName
 * @property {string} businessId
 * @property {string} [businessLogo]
 * @property {'Basic'|'Pro'} currentPlan
 * @property {'Trial'|'Active'|'Grace Period'|'Inactive'} subscriptionStatus
 * @property {ScheduledChangeType} scheduledChangeType
 * @property {string|null} scheduledChangeRequestedAt ISO date
 * @property {string|null} scheduledChangeEffectiveAt ISO date (date-only) when scheduled
 * @property {string|null} currentSubscriptionStartDate ISO date
 * @property {string|null} currentPeriodStart ISO date
 * @property {string|null} currentPeriodEnd ISO date
 * @property {string|null} trialEndDate ISO date — Trial only
 * @property {string|null} gracePeriodEndDate ISO date — Grace Period only
 * @property {string|null} nextBillingDate ISO date
 * @property {number} monthlyAmount
 * @property {string} gstTreatment
 * @property {string|null} paymentBrand
 * @property {string|null} paymentLast4
 * @property {string|null} paymentExpiry
 * @property {string} paymentProvider
 * @property {string} lastPaymentStatus
 * @property {string|null} lastPaymentDate ISO datetime
 * @property {string} stripeCustomerId
 * @property {number} quoteUsedThisCycle
 * @property {string|null} quoteResetsOn ISO date
 * @property {number|null} [quoteCarriedAllowance] Grace Period: carried allowance from previous paid cycle
 */

/** @type {BusinessSubscriptionRecord[]} */
export const businessSubscriptions = [
  {
    id: 'sub-werribee',
    businessName: 'Werribee Plumbing Pty Ltd',
    businessId: 'BUS-9934',
    businessLogo: 'https://i.pravatar.cc/120?img=32',
    currentPlan: 'Pro',
    subscriptionStatus: 'Active',
    scheduledChangeType: 'None',
    scheduledChangeRequestedAt: null,
    scheduledChangeEffectiveAt: null,
    currentSubscriptionStartDate: '2024-03-20',
    currentPeriodStart: '2026-08-07',
    currentPeriodEnd: '2026-09-07',
    trialEndDate: null,
    gracePeriodEndDate: null,
    nextBillingDate: '2026-09-07',
    monthlyAmount: 19.99,
    gstTreatment: 'GST inclusive',
    paymentBrand: 'Visa',
    paymentLast4: '4242',
    paymentExpiry: '11/2028',
    paymentProvider: 'Stripe',
    lastPaymentStatus: 'Succeeded',
    lastPaymentDate: '2026-08-07T06:00:00+10:00',
    stripeCustomerId: 'cus_R8F3W9934',
    quoteUsedThisCycle: 8,
    quoteResetsOn: '2026-09-07',
  },
  {
    id: 'sub-apex',
    businessName: 'Apex Drainage Services',
    businessId: 'BUS-7142',
    businessLogo: 'https://i.pravatar.cc/120?img=12',
    currentPlan: 'Basic',
    subscriptionStatus: 'Trial',
    scheduledChangeType: 'None',
    scheduledChangeRequestedAt: null,
    scheduledChangeEffectiveAt: null,
    currentSubscriptionStartDate: '2026-07-20',
    currentPeriodStart: '2026-07-20',
    currentPeriodEnd: '2026-08-17',
    trialEndDate: '2026-08-17',
    gracePeriodEndDate: null,
    nextBillingDate: '2026-08-17',
    monthlyAmount: 9.99,
    gstTreatment: 'GST inclusive',
    paymentBrand: null,
    paymentLast4: null,
    paymentExpiry: null,
    paymentProvider: 'Stripe',
    lastPaymentStatus: 'No Payment Yet',
    lastPaymentDate: null,
    stripeCustomerId: 'cus_R8F3W7142',
    quoteUsedThisCycle: 1,
    quoteResetsOn: '2026-08-17',
  },
  {
    id: 'sub-urban',
    businessName: 'Urban Tree Services',
    businessId: 'BUS-3625',
    businessLogo: 'https://i.pravatar.cc/120?img=18',
    currentPlan: 'Basic',
    subscriptionStatus: 'Grace Period',
    scheduledChangeType: 'None',
    scheduledChangeRequestedAt: null,
    scheduledChangeEffectiveAt: null,
    currentSubscriptionStartDate: '2025-11-12',
    currentPeriodStart: '2026-08-26',
    currentPeriodEnd: '2026-09-26',
    trialEndDate: null,
    gracePeriodEndDate: '2026-09-02',
    nextBillingDate: null,
    monthlyAmount: 9.99,
    gstTreatment: 'GST inclusive',
    paymentBrand: 'Mastercard',
    paymentLast4: '5521',
    paymentExpiry: '08/2027',
    paymentProvider: 'Stripe',
    lastPaymentStatus: 'Failed',
    lastPaymentDate: '2026-08-26T14:22:00+10:00',
    stripeCustomerId: 'cus_R8F3W3625',
    quoteUsedThisCycle: 4,
    quoteResetsOn: null,
    quoteCarriedAllowance: 4,
  },
  {
    id: 'sub-northside',
    businessName: 'Northside Electrical',
    businessId: 'BUS-5810',
    businessLogo: 'https://i.pravatar.cc/120?img=45',
    currentPlan: 'Pro',
    subscriptionStatus: 'Active',
    scheduledChangeType: 'Cancellation',
    scheduledChangeRequestedAt: '2026-08-10',
    scheduledChangeEffectiveAt: '2026-09-01',
    currentSubscriptionStartDate: '2024-06-01',
    currentPeriodStart: '2026-08-01',
    currentPeriodEnd: '2026-09-01',
    trialEndDate: null,
    gracePeriodEndDate: null,
    nextBillingDate: null,
    monthlyAmount: 19.99,
    gstTreatment: 'GST inclusive',
    paymentBrand: 'Visa',
    paymentLast4: '6610',
    paymentExpiry: '03/2029',
    paymentProvider: 'Stripe',
    lastPaymentStatus: 'Succeeded',
    lastPaymentDate: '2026-08-01T09:15:00+10:00',
    stripeCustomerId: 'cus_R8F3W5810',
    quoteUsedThisCycle: 11,
    quoteResetsOn: '2026-09-01',
  },
  {
    id: 'sub-rapid',
    businessName: 'Rapid Roofing Specialists',
    businessId: 'BUS-4478',
    businessLogo: 'https://i.pravatar.cc/120?img=28',
    currentPlan: 'Basic',
    subscriptionStatus: 'Inactive',
    scheduledChangeType: 'None',
    scheduledChangeRequestedAt: null,
    scheduledChangeEffectiveAt: null,
    currentSubscriptionStartDate: '2025-02-14',
    currentPeriodStart: null,
    currentPeriodEnd: null,
    trialEndDate: null,
    gracePeriodEndDate: null,
    nextBillingDate: null,
    monthlyAmount: 9.99,
    gstTreatment: 'GST inclusive',
    paymentBrand: 'Mastercard',
    paymentLast4: '4242',
    paymentExpiry: '01/2027',
    paymentProvider: 'Stripe',
    lastPaymentStatus: 'Failed',
    lastPaymentDate: '2026-07-28T11:40:00+10:00',
    stripeCustomerId: 'cus_R8F3W4478',
    quoteUsedThisCycle: 0,
    quoteResetsOn: null,
  },
];

export function getQuotePlanAllowance(recordOrPlan) {
  if (recordOrPlan && typeof recordOrPlan === 'object') {
    if (
      recordOrPlan.subscriptionStatus === 'Grace Period' &&
      recordOrPlan.quoteCarriedAllowance != null
    ) {
      return Number(recordOrPlan.quoteCarriedAllowance) || 0;
    }
    return QUOTE_DELIVERY_ALLOWANCE_BY_PLAN[recordOrPlan.currentPlan] ?? 0;
  }
  return QUOTE_DELIVERY_ALLOWANCE_BY_PLAN[recordOrPlan] ?? 0;
}

export function getQuoteRemaining(record) {
  const allowance = getQuotePlanAllowance(record);
  const used = Number(record?.quoteUsedThisCycle) || 0;
  return Math.max(0, allowance - used);
}

export function formatQuotePlanAllowance(recordOrPlan) {
  const allowance = getQuotePlanAllowance(recordOrPlan);
  if (!allowance) return '—';
  return `${allowance} Quote deliveries`;
}

export function formatSubscriptionDate(isoDate) {
  if (!isoDate) return '—';
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Date + time display for payment timestamps (matches Transactions / other admin tables). */
export function formatSubscriptionDateTime(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatCurrentPeriod(record) {
  if (!record?.currentPeriodStart || !record?.currentPeriodEnd) return '—';
  return `${formatSubscriptionDate(record.currentPeriodStart)}–${formatSubscriptionDate(record.currentPeriodEnd)}`;
}

export function formatPaymentMethod(record) {
  if (!record?.paymentBrand || !record?.paymentLast4) return 'Not provided';
  return `${record.paymentBrand} •••• ${record.paymentLast4}`;
}

export function hasPaymentMethod(record) {
  return Boolean(record?.paymentBrand && record?.paymentLast4);
}

export function formatMonthlyAmount(amount) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(amount ?? 0);
}

/** e.g. $19.99 AUD */
export function formatMonthlyAmountWithCurrency(amount) {
  return `${formatMonthlyAmount(amount)} AUD`;
}

/** e.g. Cancellation · 01 Sep 2026 */
export function formatScheduledChange(record) {
  if (!record || record.scheduledChangeType === 'None' || !record.scheduledChangeType) {
    return 'None';
  }
  const effective = formatSubscriptionDate(record.scheduledChangeEffectiveAt);
  if (!record.scheduledChangeEffectiveAt || effective === '—') {
    return record.scheduledChangeType;
  }
  return `${record.scheduledChangeType} · ${effective}`;
}

export function getBusinessSubscriptionByBusinessId(businessId) {
  if (!businessId) return null;
  const id = String(businessId).trim().toLowerCase();
  return (
    businessSubscriptions.find(
      (row) =>
        String(row.businessId).toLowerCase() === id || String(row.id).toLowerCase() === id
    ) || null
  );
}
